import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18nGlobal from "../i18n";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ImageUpload from "../components/ImageUpload";
import ProfileBadges from "../components/ProfileBadges";
import VerificationProgress from "../components/VerificationProgress";
import TrustScoreCard from "../components/TrustScoreCard";
import AppearanceCard from "../components/AppearanceCard";
import ProfilePhotoCropper from "../components/ProfilePhotoCropper";
import DocumentCard from "../components/DocumentCard";
import {
  User,
  Shield,
  Camera,
  Check,
  Clock,
  X,
  Loader2,
  Car,
  AlertCircle,
  FileText,
  Home as HomeIcon,
  ClipboardList,
  CreditCard,
  IdCard,
  ScanLine,
  Trash2,
  Download,
  Send,
  Crown,
  Copy,
  Share2,
} from "lucide-react";
import { toast } from "sonner";

const statusConfig = {
  approved: { icon: Check, label: "status.approved", className: "bg-green-100 text-green-700" },
  pending: { icon: Clock, label: "status.pending", className: "bg-yellow-100 text-yellow-700" },
  rejected: { icon: X, label: "status.rejected", className: "bg-red-100 text-red-700" },
  needs_info: { icon: AlertCircle, label: "status.needsInfo", className: "bg-blue-100 text-blue-700" },
  not_started: { icon: AlertCircle, label: "status.notStarted", className: "bg-neutral-100 text-neutral-700" },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.not_started;
  const Icon = cfg.icon;
  return (
    <Badge className={cfg.className} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3 mr-1" />
      {i18nGlobal.t(cfg.label)}
    </Badge>
  );
}

function ChecklistRow({ label, done }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center ${
          done ? "bg-green-600 text-white" : "bg-neutral-200 text-neutral-400"
        }`}
      >
        {done ? <Check className="w-3 h-3" /> : <div className="w-1.5 h-1.5 rounded-full bg-current" />}
      </div>
      <span className={done ? "text-neutral-900" : "text-neutral-500"}>{label}</span>
    </div>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "" });

  // Host ownership verification form
  const [hostData, setHostData] = useState({
    proof_of_address: "",
    vehicle_ownership_doc: "",
  });
  const [submittingHost, setSubmittingHost] = useState(false);
  const [submittingId, setSubmittingId] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [profileMode, setProfileMode] = useState("guest");
  const [switchingMode, setSwitchingMode] = useState(false);
  const [profileBadges, setProfileBadges] = useState(null);
  const [docBadges, setDocBadges] = useState(null);
  const [trustScore, setTrustScore] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name || "", phone: user.phone || "" });
      setHostData({
        proof_of_address: user.proof_of_address || "",
        vehicle_ownership_doc: user.vehicle_ownership_doc || "",
      });
      // Initial mode from server; default to "host" if user is a host/admin
      const initial =
        user.profile_mode ||
        (user.role === "host" || user.role === "admin" ? "host" : "guest");
      setProfileMode(initial);

      // Fetch achievement badges (re-uses public profile endpoint)
      api
        .get(`/users/${user.user_id}/profile`)
        .then((res) => {
          setProfileBadges(res.data?.profile_badges || null);
          setDocBadges(res.data?.badges || null);
          setTrustScore(res.data?.trust_score || null);
        })
        .catch(() => {
          setProfileBadges(null);
          setDocBadges(null);
          setTrustScore(null);
        });
    }
  }, [user]);

  // VIP membership Stripe-return handler. Runs once on mount when ?vip=success&session_id=... is present.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const vipParam = searchParams.get("vip");
    const sessionId = searchParams.get("session_id");
    if (vipParam === "success" && sessionId) {
      let cancelled = false;
      api
        .get(`/users/me/vip/verify?session_id=${encodeURIComponent(sessionId)}`)
        .then((res) => {
          if (cancelled) return;
          if (res.data?.vip_active) {
            const bonus = res.data?.referral_bonus_days || 0;
            if (bonus > 0) {
              toast.success(
                `¡Membresía VIP activada! Tú y tu referente ganaron ${bonus} días gratis 🎉`
              );
            } else {
              toast.success("¡Membresía VIP activada! Premium desbloqueado.");
            }
          } else {
            toast.info("Estamos confirmando tu pago. Vuelve a la página en unos momentos.");
          }
          // Clean URL
          searchParams.delete("vip");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
        })
        .catch(() => {
          if (!cancelled) toast.error("No pudimos verificar tu pago VIP.");
        });
      return () => {
        cancelled = true;
      };
    }
    if (vipParam === "canceled") {
      toast.info("Pago de VIP cancelado.");
      searchParams.delete("vip");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const canHostMode = user?.role === "host" || user?.role === "admin";

  const handleSwitchMode = async (next) => {
    if (next === profileMode || switchingMode) return;
    if (next === "host" && !canHostMode) {
      toast.info(t("profile.toastBecomeHostFirst"));
      return;
    }
    setSwitchingMode(true);
    const prev = profileMode;
    setProfileMode(next); // optimistic
    try {
      await api.put("/auth/profile-mode", { profile_mode: next });
      toast.success(next === "host" ? t("profile.toastModeHost") : t("profile.toastModeGuest"));
    } catch (err) {
      setProfileMode(prev);
      toast.error(err.response?.data?.detail || t("profile.toastModeFailed"));
    } finally {
      setSwitchingMode(false);
    }
  };

  const idStatus = user?.verification_status || "not_started";
  const hostStatus = user?.host_verification_status || "not_started";

  // Per-document state from server (verification_docs dict)
  const verificationDocs = user?.verification_docs || {};
  const verificationSubmittedAt = user?.verification_submitted_at || null;
  const isPasswordUser = user?.auth_provider === "password";
  const ID_DOCS = [
    {
      key: "government_id_front",
      title: t("profile.docIdFront"),
      description: t("profile.docIdFrontSub"),
      icon: IdCard,
      required: true,
    },
    {
      key: "government_id_back",
      title: t("profile.docIdBack"),
      description: t("profile.docIdBackSub"),
      icon: IdCard,
      required: false,
    },
    {
      key: "driver_license_front",
      title: t("profile.docLicFront"),
      description: t("profile.docLicFrontSub"),
      icon: CreditCard,
      required: true,
    },
    {
      key: "driver_license_back",
      title: t("profile.docLicBack"),
      description: t("profile.docLicBackSub"),
      icon: CreditCard,
      required: true,
    },
    {
      key: "selfie",
      title: t("profile.docSelfie"),
      description: t("profile.docSelfieSub"),
      icon: ScanLine,
      required: true,
    },
  ];

  const idDone = ID_DOCS.filter((d) => verificationDocs[d.key]?.url).length;
  const idTotal = ID_DOCS.length;
  const idPct = Math.round((idDone / idTotal) * 100);
  const idRequiredDone = ID_DOCS.filter((d) => d.required && verificationDocs[d.key]?.url).length;
  const idRequiredTotal = ID_DOCS.filter((d) => d.required).length;

  const hostSteps = [
    { key: "proof_of_address", label: t("profile.proofOfAddress") },
    { key: "vehicle_ownership_doc", label: t("profile.ownershipDoc") },
  ];
  const hostDone = hostSteps.filter((s) => hostData[s.key]).length;
  const hostPct = Math.round((hostDone / hostSteps.length) * 100);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/auth/profile", formData);
      await checkAuth();
      toast.success(t("profile.toastProfileUpdated"));
    } catch {
      toast.error(t("profile.toastProfileFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitHost = async () => {
    if (!hostData.proof_of_address || !hostData.vehicle_ownership_doc) {
      toast.error(t("profile.toastCompleteDocs"));
      return;
    }
    setSubmittingHost(true);
    try {
      await api.post("/auth/host-verification", hostData);
      await checkAuth();
      toast.success(t("profile.toastHostDocsSent"));
    } catch {
      toast.error(t("profile.toastDocsFailed"));
    } finally {
      setSubmittingHost(false);
    }
  };

  const handleBecomeHost = async () => {
    try {
      await api.post("/auth/become-host");
      await checkAuth();
      toast.success(t("profile.toastNowHost"));
    } catch {
      toast.error(t("profile.toastRoleFailed"));
    }
  };

  const handleSubmitIdentity = async () => {
    setSubmittingId(true);
    try {
      await api.post("/auth/verification/submit");
      await checkAuth();
      toast.success(t("profile.toastDocsSent"));
    } catch (e) {
      const msg = e?.response?.data?.detail || t("profile.toastDocsFailed");
      toast.error(typeof msg === "string" ? msg : t("profile.toastDocsFailed"));
    } finally {
      setSubmittingId(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const body = isPasswordUser
        ? { password: deletePassword }
        : { confirmation: deleteConfirm };
      await api.delete("/auth/account", { data: body });
      toast.success(t("profile.toastAccountDeleted"));
      setDeleteOpen(false);
      // Clear local auth and redirect home
      try { await logout(); } catch { /* already logged out server-side */ }
      navigate("/");
    } catch (e) {
      const msg = e?.response?.data?.detail || t("profile.toastDeleteFailed");
      toast.error(typeof msg === "string" ? msg : t("profile.toastDeleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  // Host docs are still bulk-submit (existing behaviour); identity docs are now per-doc auto-save.
  const hostEditable = hostStatus !== "approved" && hostStatus !== "pending";

  return (
    <div className="min-h-screen bg-sand" data-testid="profile-page">
      <div className="container-main py-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-8">{t("profile.title")}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-terracotta" />
                {t("profile.personalInfo")}
              </h3>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setPhotoOpen(true)}
                    className="relative group rounded-full focus:outline-none focus:ring-2 focus:ring-terracotta"
                    data-testid="change-photo-btn"
                  >
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-terracotta text-white text-2xl">
                        {user?.name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                      <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                  <div>
                    <p className="font-semibold text-lg">{user?.name}</p>
                    <p className="text-gray-500 text-sm">{user?.email}</p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <StatusBadge status={idStatus} />
                      {(user?.role === "host" || user?.role === "admin") && (
                        <StatusBadge status={hostStatus} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-2">
                  <ProfileBadges
                    badges={profileBadges}
                    testid="own-profile-badges"
                  />
                </div>
                <div className="mt-4">
                  <VerificationProgress
                    badges={docBadges}
                    forHost={user?.role === "host" || user?.role === "admin"}
                  />
                </div>
                <div>
                  <Label htmlFor="name">{t("profile.fullName")}</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-2 input-base" data-testid="profile-name-input" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={user?.email || ""} disabled className="mt-2 input-base bg-gray-100" />
                </div>
                <div>
                  <Label htmlFor="phone">{t("profile.phone")}</Label>
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+52 664 123 4567" className="mt-2 input-base" data-testid="profile-phone-input" />
                </div>
                <Button type="submit" disabled={loading} className="btn-primary" data-testid="save-profile-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("profile.saveChanges")}
                </Button>
              </form>
            </div>

            {/* Seguridad shortcut */}
            <button
              type="button"
              onClick={() => navigate("/seguridad")}
              className="card-base w-full text-left flex items-center gap-4 hover:border-terracotta/40 hover:shadow-md transition-all"
              data-testid="profile-security-link"
            >
              <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-lg text-agave">
                  {t("profile.securityTitle")}
                </h3>
                <p className="text-sm text-neutral-600">
                  {t("profile.securitySub")}
                </p>
              </div>
              <span className="text-terracotta text-sm font-medium hidden sm:inline">{t("profile.open")}</span>
            </button>

            {/* VIP Membership + Referral */}
            <VipMembershipCard />

            {/* Trust Score */}
            {trustScore && <TrustScoreCard trust={trustScore} testid="profile-trust-score" />}
            <AppearanceCard />

            {/* Identity Verification (Guest) */}
            <div className="card-base" data-testid="identity-verification-section">
              <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                  <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-terracotta" />
                    {t("profile.idVerification")}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t("profile.idVerificationSub")}
                  </p>
                </div>
                <StatusBadge status={idStatus} />
              </div>

              {/* Aggregate banners */}
              {idStatus === "approved" && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-green-800">{t("profile.verifDone")}</p>
                    <p className="text-green-700">{t("profile.verifDoneSub")}</p>
                  </div>
                </div>
              )}
              {idStatus === "pending" && idDone === idTotal && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-800">{t("profile.inReview")}</p>
                    <p className="text-amber-700">{t("profile.inReviewSub")}</p>
                  </div>
                </div>
              )}
              {idStatus === "rejected" && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-red-800">{t("profile.docRejected")}</p>
                    <p className="text-red-700">{t("profile.docRejectedSub")}</p>
                  </div>
                </div>
              )}

              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                  <span>{t("profile.progress")}</span>
                  <span className="font-semibold" data-testid="identity-progress-count">
                    {t("profile.docsUploaded", { done: idDone, total: idTotal })}
                  </span>
                </div>
                <div className="w-full bg-neutral-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-[#111111] transition-all"
                    style={{ width: `${idPct}%` }}
                    data-testid="identity-progress-bar"
                  />
                </div>
              </div>

              {/* 5 Document Cards */}
              <div className="space-y-3">
                {ID_DOCS.map((d) => {
                  const docState = verificationDocs[d.key];
                  const docStatus = docState?.status;
                  // Locked when overall verification has been finalized AND this specific doc
                  // is not awaiting re-upload (rejected / needs_info).
                  const docLocked =
                    !!verificationSubmittedAt &&
                    docStatus !== "rejected" &&
                    docStatus !== "needs_info";
                  return (
                    <DocumentCard
                      key={d.key}
                      title={d.title}
                      description={d.description}
                      icon={d.icon}
                      docKey={d.key}
                      imageType={d.key}
                      required={d.required}
                      doc={docState}
                      locked={docLocked}
                      onUploaded={() => checkAuth()}
                    />
                  );
                })}
              </div>

              {/* Submit / finalize button */}
              {!verificationSubmittedAt && (
                <div className="mt-4">
                  <Button
                    type="button"
                    onClick={handleSubmitIdentity}
                    disabled={submittingId || idRequiredDone < idRequiredTotal}
                    className="w-full bg-[#111111] hover:bg-neutral-800 text-white h-11 rounded-xl"
                    data-testid="submit-identity-verification-btn"
                  >
                    {submittingId ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {t("profile.submitDocs")}
                  </Button>
                  <p className="text-xs text-neutral-500 mt-2 text-center">
                    {t("profile.submitDocsNote")}
                  </p>
                </div>
              )}
              {verificationSubmittedAt && (
                <div
                  className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3"
                  data-testid="identity-locked-banner"
                >
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-800">
                      {t("profile.docsSubmitted")}
                    </p>
                    <p className="text-amber-700">
                      {t("profile.docsLockedNote")}
                    </p>
                  </div>
                </div>
              )}

              {/* Compliance disclaimer */}
              <p
                className="text-xs text-neutral-500 mt-4 flex items-start gap-1.5"
                data-testid="identity-disclaimer"
              >
                <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                {t("profile.docsSafeNote")}
              </p>
              <p
                className="text-xs text-neutral-500 mt-1 flex items-start gap-1.5"
                data-testid="identity-ai-precheck-hint"
              >
                <span className="inline-block w-3.5 h-3.5 mt-0.5 flex-shrink-0">✨</span>
                {t("profile.aiPrecheckNote")}
              </p>
            </div>

            {/* Host Ownership Verification */}
            {(user?.role === "host" || user?.role === "admin") && (
              <div className="card-base" data-testid="host-verification-section">
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div>
                    <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-terracotta" />
                      {t("profile.hostVerification")}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t("profile.hostVerificationSub")}
                    </p>
                  </div>
                  <StatusBadge status={hostStatus} />
                </div>

                {hostStatus === "rejected" && user?.host_verification_rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-red-800">{t("profile.rejectionReason")}</p>
                      <p className="text-red-700">{user.host_verification_rejection_reason}</p>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                    <span>{t("profile.progress")}</span>
                    <span className="font-semibold">{hostDone}/{hostSteps.length}</span>
                  </div>
                  <div className="w-full bg-neutral-100 h-1.5 rounded-full overflow-hidden">
                    <div className="h-1.5 bg-[#111111] transition-all" style={{ width: `${hostPct}%` }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {hostSteps.map((s) => (
                      <ChecklistRow key={s.key} label={s.label} done={!!hostData[s.key]} />
                    ))}
                  </div>
                </div>

                {hostStatus === "approved" ? (
                  <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
                    <Check className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">{t("profile.ownerVerified")}</p>
                      <p className="text-sm text-green-600">{t("profile.ownerVerifiedSub")}</p>
                    </div>
                  </div>
                ) : hostStatus === "pending" ? (
                  <div className="bg-yellow-50 rounded-xl p-4 flex items-center gap-3">
                    <Clock className="w-6 h-6 text-yellow-600" />
                    <div>
                      <p className="font-semibold text-yellow-800">{t("profile.inReview")}</p>
                      <p className="text-sm text-yellow-600">{t("profile.inReviewSub")}</p>
                    </div>
                  </div>
                ) : null}

                {hostEditable && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="mb-2 block flex items-center gap-2">
                        <HomeIcon className="w-4 h-4" /> {t("profile.proofOfAddress")}
                      </Label>
                      <ImageUpload
                        imageType="proof_of_address"
                        maxImages={1}
                        existingImages={hostData.proof_of_address ? [{ id: "poa", url: hostData.proof_of_address, data: hostData.proof_of_address }] : []}
                        onImageUploaded={(imgs) => setHostData({ ...hostData, proof_of_address: imgs[0] || "" })}
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block flex items-center gap-2">
                        <FileText className="w-4 h-4" /> {t("profile.ownershipDocFull")}
                      </Label>
                      <ImageUpload
                        imageType="vehicle_ownership"
                        maxImages={1}
                        existingImages={hostData.vehicle_ownership_doc ? [{ id: "voc", url: hostData.vehicle_ownership_doc, data: hostData.vehicle_ownership_doc }] : []}
                        onImageUploaded={(imgs) => setHostData({ ...hostData, vehicle_ownership_doc: imgs[0] || "" })}
                      />
                    </div>
                    <Button onClick={handleSubmitHost} disabled={submittingHost} className="btn-primary" data-testid="submit-host-verification-btn">
                      {submittingHost ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                      {hostStatus === "rejected" ? t("profile.resendDocs") : t("profile.sendDocs")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div className="card-base" data-testid="account-mode-card">
              <h3 className="font-heading font-semibold text-lg mb-1">{t("profile.accountType")}</h3>
              <p className="text-sm text-neutral-500 mb-4">
                {t("profile.accountTypeSub")}
              </p>

              {canHostMode ? (
                // Segmented toggle for hosts/admins
                <div
                  role="tablist"
                  aria-label="Tipo de cuenta"
                  className="relative grid grid-cols-2 bg-neutral-100 rounded-2xl p-1 select-none"
                  data-testid="profile-mode-toggle"
                >
                  {/* Sliding pill */}
                  <span
                    aria-hidden
                    className={`absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-xl bg-white shadow-sm transition-transform duration-300 ease-out will-change-transform ${
                      profileMode === "host" ? "translate-x-full" : "translate-x-0"
                    }`}
                  />
                  <button
                    role="tab"
                    type="button"
                    aria-selected={profileMode === "guest"}
                    disabled={switchingMode}
                    onClick={() => handleSwitchMode("guest")}
                    data-testid="mode-tab-guest"
                    className={`relative z-[1] py-2.5 px-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                      profileMode === "guest" ? "text-[#111111]" : "text-neutral-500 hover:text-neutral-700"
                    }`}
                  >
                    <User className="w-4 h-4" />
                    {t("profile.userTab")}
                  </button>
                  <button
                    role="tab"
                    type="button"
                    aria-selected={profileMode === "host"}
                    disabled={switchingMode}
                    onClick={() => handleSwitchMode("host")}
                    data-testid="mode-tab-host"
                    className={`relative z-[1] py-2.5 px-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                      profileMode === "host" ? "text-[#111111]" : "text-neutral-500 hover:text-neutral-700"
                    }`}
                  >
                    <Car className="w-4 h-4" />
                    Host
                  </button>
                </div>
              ) : (
                // Pure guest — keep the "Convertirme en Anfitrión" path
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-agave">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{t("profile.guest")}</p>
                      <p className="text-sm text-gray-500">{t("profile.guestSub")}</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleBecomeHost}
                    className="w-full btn-outline"
                    data-testid="become-host-btn"
                  >
                    <Car className="w-4 h-4 mr-2" />
                    {t("profile.becomeHost")}
                  </Button>
                </div>
              )}

              {/* Active mode summary */}
              {canHostMode && (
                <div
                  className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200"
                  data-testid={`mode-summary-${profileMode}`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      profileMode === "host" ? "bg-terracotta" : "bg-agave"
                    }`}
                  >
                    {profileMode === "host" ? (
                      <Car className="w-4 h-4 text-white" />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold capitalize">
                      {profileMode === "host"
                        ? user?.role === "admin"
                          ? t("profile.adminHost")
                          : t("profile.host")
                        : t("profile.guest")}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {profileMode === "host"
                        ? t("profile.hostModeSub")
                        : t("profile.guestModeSub")}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="card-base" data-testid={`stats-card-${profileMode}`}>
              <h3 className="font-heading font-semibold text-lg mb-1">{t("profile.stats")}</h3>
              <p className="text-xs text-neutral-500 mb-4">
                {profileMode === "host" ? t("profile.hostView") : t("profile.guestView")}
              </p>
              {profileMode === "host" && canHostMode ? (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("profile.activeVehicles")}</span>
                    <span className="font-semibold">{user?.active_vehicles ?? 0}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("profile.completedBookings")}</span>
                    <span className="font-semibold">{user?.host_completed_trips ?? 0}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("profile.hostRating")}</span>
                    <span className="font-semibold">
                      {user?.host_rating != null ? `★ ${Number(user.host_rating).toFixed(1)}` : "—"}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("profile.memberSince")}</span>
                    <span className="font-semibold">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "short" }) : "-"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("profile.completedTrips")}</span>
                    <span className="font-semibold">{user?.total_trips || 0}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("profile.memberSince")}</span>
                    <span className="font-semibold">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "short" }) : "-"}
                    </span>
                  </div>
                </div>
              )}

              {/* Quick links per mode */}
              <div className="mt-5 grid grid-cols-2 gap-2">
                {profileMode === "host" && canHostMode ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/host/listings")}
                      data-testid="mode-link-listings"
                      className="h-10 rounded-xl text-xs"
                    >
                      {t("profile.myVehicles")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/host/calendar")}
                      data-testid="mode-link-calendar"
                      className="h-10 rounded-xl text-xs"
                    >
                      {t("profile.calendar")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/host/analytics")}
                      data-testid="mode-link-analytics"
                      className="h-10 rounded-xl text-xs"
                    >
                      {t("profile.earnings")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/host/dashboard")}
                      data-testid="mode-link-host-dashboard"
                      className="h-10 rounded-xl text-xs"
                    >
                      {t("profile.bookings")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/host/seguro")}
                      data-testid="mode-link-host-insurance"
                      className="h-10 rounded-xl text-xs col-span-2"
                    >
                      {t("profile.multiInsurance")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/trips")}
                      data-testid="mode-link-trips"
                      className="h-10 rounded-xl text-xs"
                    >
                      {t("profile.myTrips")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/inbox")}
                      data-testid="mode-link-inbox"
                      className="h-10 rounded-xl text-xs"
                    >
                      {t("profile.messages")}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {user?.role !== "admin" && (
              <div className="card-base" data-testid="privacy-section">
                <h3 className="font-heading font-semibold text-lg mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-neutral-600" />
                  {t("profile.privacyTitle")}
                </h3>
                <div className="space-y-2">
                  <a
                    href="/privacidad"
                    className="block text-sm text-neutral-600 hover:text-neutral-900 underline"
                    data-testid="privacy-notice-link"
                  >
                    {t("profile.privacyNotice")}
                  </a>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await api.get("/account-export", { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "rivones-data-export.json";
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success(t("profile.exportReady"));
                      } catch (e) {
                        toast.error(t("profile.exportError"));
                      }
                    }}
                    className="w-full"
                    data-testid="privacy-export-data-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("profile.exportData")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/eliminar-cuenta?email=${encodeURIComponent(user?.email || "")}`)}
                    className="w-full border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="privacy-delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t("profile.deleteAccountPermanent")}
                  </Button>
                  <p className="text-xs text-neutral-500">{t("profile.deleteAccountPermanentHint")}</p>
                </div>
              </div>
            )}

            {user?.role !== "admin" && (
              <div
                className="card-base border-red-200"
                data-testid="danger-zone"
              >
                <h3 className="font-heading font-semibold text-lg mb-2 text-red-700 flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  {t("profile.dangerZone")}
                </h3>
                <p className="text-sm text-neutral-600 mb-4">
                  {t("profile.dangerBody")}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setDeleteOpen(true)}
                  className="w-full border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="open-delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t("profile.deleteAccount")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete account confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) { setDeletePassword(""); setDeleteConfirm(""); } }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              {t("profile.deleteTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("profile.deleteDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {isPasswordUser ? (
              <div>
                <Label htmlFor="delete-password" className="font-semibold">
                  {t("profile.confirmPassword")}
                </Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1"
                  data-testid="delete-password-input"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="delete-confirm" className="font-semibold">
                  {t("profile.typeToConfirm")} <span className="font-mono text-red-700">ELIMINAR</span> {t("profile.toConfirm")}
                </Label>
                <Input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="ELIMINAR"
                  className="mt-1 font-mono"
                  data-testid="delete-confirm-input"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              data-testid="delete-cancel-btn"
            >
              {t("profile.cancel")}
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={
                deleting ||
                (isPasswordUser ? !deletePassword : deleteConfirm.trim().toUpperCase() !== "ELIMINAR")
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {t("profile.deleteForever")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProfilePhotoCropper
        open={photoOpen}
        onOpenChange={setPhotoOpen}
        onUploaded={async () => {
          await checkAuth();
        }}
      />
    </div>
  );
}


function VipMembershipCard() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/users/me/vip/referral")
      .then(({ data }) => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info) return null;
  // Hide the card entirely for non-VIPs (we still surface VIP via checkout)
  if (!info.is_vip_active || !info.referral_code) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(info.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const handleShare = async () => {
    const shareText = `Únete a RivoNes con mi código VIP ${info.referral_code} — los dos ganamos 30 días gratis.`;
    const url = "https://rivones.com/profile?vip=signup";
    if (navigator.share) {
      try {
        await navigator.share({ title: "RivoNes VIP", text: shareText, url });
      } catch {
        // user canceled
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText} ${url}`);
        toast.success("Texto copiado");
      } catch {
        // ignore
      }
    }
  };

  const expires = info.vip_expires_at
    ? new Date(info.vip_expires_at).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div
      className="card-base relative overflow-hidden"
      data-testid="vip-membership-card"
    >
      {/* Subtle gradient accent */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-transparent to-amber-100/40 pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
            <Crown className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-lg text-agave flex items-center gap-2">
              Membresía VIP RivoNes
              <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">
                Activa
              </span>
            </h3>
            {expires && (
              <p className="text-xs text-neutral-600">
                Vigente hasta el <strong>{expires}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Referral code block */}
        <div className="bg-white/80 backdrop-blur-sm border border-amber-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-2">
            Tu código de referido
          </p>
          <div className="flex items-center gap-2">
            <div
              className="flex-1 font-mono text-2xl font-bold tracking-widest text-agave bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center"
              data-testid="vip-my-referral-code"
            >
              {info.referral_code}
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="btn-secondary px-3 py-2 flex-shrink-0"
              data-testid="vip-copy-code-btn"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md flex-shrink-0"
              data-testid="vip-share-code-btn"
              aria-label="Compartir"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-neutral-600 mt-3 leading-relaxed">
            Comparte este código con cualquier amigo que active VIP. Cuando lo
            usen, ustedes dos ganan{" "}
            <strong>{info.bonus_days_per_referral} días gratis</strong> agregados
            a su membresía.
          </p>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="text-center bg-white/60 rounded-lg py-2">
            <p className="text-2xl font-bold text-amber-600" data-testid="vip-total-refs">
              {info.total_referrals}
            </p>
            <p className="text-[11px] text-neutral-600 uppercase tracking-wider">
              Referidos
            </p>
          </div>
          <div className="text-center bg-white/60 rounded-lg py-2">
            <p className="text-2xl font-bold text-emerald-600" data-testid="vip-total-bonus-days">
              +{info.total_bonus_days_earned}
            </p>
            <p className="text-[11px] text-neutral-600 uppercase tracking-wider">
              Días bonus
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
